// ─────────────────────────────────────────────────────────────────────────────
// Pure metric helpers for the Management dashboard. Kept separate from the React
// component so the numbers (conversion, coverage, honest QoQ deltas) are unit-
// testable without rendering. No theme/JSX here — colours stay in the component.
// ─────────────────────────────────────────────────────────────────────────────

// Whole-number percentage; 0 when the denominator is missing/zero.
export const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

// Aggregate the filtered shows into the headline numbers.
// found=companies, enriched=companies on enriched shows, reviewed/crm/campaign
// summed from the sales/marketing assignment lookups.
export function aggregateMetrics(fShows, salesByShow, mktByShow) {
  const found    = fShows.reduce((a, s) => a + (s.companiesFound || 0), 0);
  const enriched = fShows.reduce((a, s) => a + (s.enrichStatus === "Enriched" ? (s.companiesFound || 0) : 0), 0);
  const reviewed = fShows.reduce((a, s) => a + ((salesByShow[s.id]?.reviewed_count) || 0), 0);
  const crm      = fShows.reduce((a, s) => a + ((salesByShow[s.id]?.approved_count) || 0), 0);
  const campaign = fShows.reduce((a, s) => a + ((mktByShow[s.id]?.pushed_count) || 0), 0);
  return { found, enriched, reviewed, crm, campaign };
}

// Per-ITE-event coverage: companies found, shows tracked, company-level reviewed.
// Sorted by companies found (desc). eventName maps an id → display name.
export function byEventCoverage(fShows, salesByShow, eventName) {
  const acc = {};
  fShows.forEach(s => {
    acc[s.event] = acc[s.event] || { key: s.event, name: eventName(s.event), found: 0, shows: 0, reviewed: 0 };
    acc[s.event].found    += s.companiesFound || 0;
    acc[s.event].shows    += 1;
    acc[s.event].reviewed += (salesByShow[s.id]?.reviewed_count) || 0;
  });
  return Object.values(acc).sort((a, b) => b.found - a.found);
}

// The show that found the most companies yet converted none — the clearest
// routing gap. Returns null when every show with companies converted at least one.
export function pickLaggard(fShows, salesByShow) {
  return fShows
    .map(s => ({ name: s.name, year: s.year, found: s.companiesFound || 0, crm: (salesByShow[s.id]?.approved_count) || 0 }))
    .filter(s => s.found > 0 && s.crm === 0)
    .sort((a, b) => b.found - a.found)[0] || null;
}

// Latest/previous quarter + honest QoQ deltas (null when there's no prior quarter).
export function yieldDeltas(trend) {
  const latestQ = trend[trend.length - 1] || null;
  const prevQ   = trend[trend.length - 2] || null;
  return {
    latestQ, prevQ,
    crmDelta:  latestQ && prevQ ? latestQ.crm - prevQ.crm : null,
    campDelta: latestQ && prevQ ? latestQ.campaign - prevQ.campaign : null,
  };
}

// KPI delta presentation — "Baseline" when there is no prior quarter.
export const deltaDir  = (d) => (d == null ? "flat" : d >= 0 ? "up" : "down");
export const deltaText = (d, prevLabel) => (d == null ? "Baseline" : `${d >= 0 ? "▲ +" : "▼ "}${Math.abs(d)} vs ${prevLabel}`);
